/**
 * Copyright (C) SiteVision AB 2002-2026, all rights reserved
 */

// Using Combobox pattern
// ----------------------
// https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Roles/combobox_role
// https://www.w3.org/WAI/ARIA/apg/patterns/combobox/examples/combobox-autocomplete-list/

import './svAutoComplete.css';

export const INPUT_SELECTOR = 'input[type="search"], input[type="text"]';

const instances = new WeakMap();

const LIST_CLASSNAME = 'sv-autocomplete-search-result';
const LIST_ITEM_CLASSNAME = 'sv-autocomplete-result-item';
const LIST_ITEM_ACTIVE_CLASSNAME = 'sv-autocomplete-result-item-active';

const QUERY_PARAM = 'term';
const CACHE_TTL = 300000;
const MIN_LENGTH = 2;
const DELAY = 200;
const SCROLL_ON_FOCUS = false;

export class SvAutoComplete {
  constructor(input, options) {
    this.options = Object.assign({}, options);
    this.element = input;
    this.form = input.form || input.closest('form');

    if (!this.element.id) {
      this.element.id = `${this.element.closest('.sv-portlet[id]')?.id}_sv-autocomplete`;
    }

    this.list = this.createList();

    this.cache = {};
    this.requestSequence = 0;
    this.abortController = null;
    this.results = [];

    this.activeIndex = -1;
    this.isOpen = false;
    this.blurTimer = null;

    this.positionList();
    this.prepareMarkup();
    this.bindEvents();
  }

  getAppendTarget() {
    const target = this.options.appendTo;

    if (!target) {
      return document.body;
    }

    if (typeof target === 'string') {
      try {
        return document.querySelector(target) || document.body;
      } catch {
        return document.body;
      }
    }

    if (target.nodeType === Node.ELEMENT_NODE) {
      return target;
    }

    if (target[0] && target[0].nodeType === Node.ELEMENT_NODE) {
      return target[0];
    }

    return document.body;
  }

  createList() {
    const list = document.createElement('ul');

    list.id = `${this.element.id}_listbox`;
    list.className = LIST_CLASSNAME;
    list.setAttribute('role', 'listbox');
    list.hidden = true;
    this.getAppendTarget().insertAdjacentElement('beforeend', list);
    return list;
  }

  updateOptions(options) {
    if (!options) {
      return;
    }

    const previousSource = this.options.source;

    Object.assign(this.options, options);

    const appendTarget = this.getAppendTarget();
    if (this.list.parentNode !== appendTarget) {
      appendTarget.insertAdjacentElement('beforeend', this.list);
    }

    if (this.options.source !== previousSource) {
      this.cache = {};
      this.requestSequence += 1;

      if (this.abortController) {
        this.abortController.abort();
      }
    }

    this.positionList();
  }

  prepareMarkup() {
    [
      'data-aria-owns',
      'data-aria-controls',
      'role',
      'aria-haspopup',
      'aria-autocomplete',
      'aria-expanded',
      'aria-controls',
      'aria-owns',
    ].forEach((name) => this.element.removeAttribute(name));
    this.element.setAttribute('role', 'combobox');
    this.element.setAttribute('aria-haspopup', 'listbox');
    this.element.setAttribute('aria-expanded', 'false');
    this.element.setAttribute('aria-controls', this.list.id);
    this.element.setAttribute('aria-autocomplete', 'list');
    this.element.setAttribute('autocomplete', 'off');
  }

  handleFocus() {
    if (this.element.value.length >= MIN_LENGTH) {
      void this.updateSuggestions(this.element.value);
    }
  }

  handleBlur() {
    this.blurTimer = window.setTimeout(() => {
      this.close();
    }, 150);
  }

  handleKeydown(evt) {
    switch (evt.key) {
      case 'ArrowDown':
        evt.preventDefault();

        if (!this.isOpen) {
          void this.updateSuggestions(this.element.value).then(() => {
            if (this.isOpen && this.activeIndex === -1) {
              this.moveActive(1);
            }
          });
          return;
        }

        this.moveActive(1);
        return;

      case 'ArrowUp':
        evt.preventDefault();

        if (!this.isOpen) {
          void this.updateSuggestions(this.element.value).then(() => {
            if (this.isOpen && this.activeIndex === -1) {
              this.moveActive(-1);
            }
          });
          return;
        }

        this.moveActive(-1);
        return;

      case 'Enter':
        if (this.isOpen && this.activeIndex >= 0) {
          evt.preventDefault();
          this.select(this.activeIndex);
          this.moveCursorToEnd();
          this.form.requestSubmit();
        }
        return;

      case 'Tab':
        if (this.isOpen && this.activeIndex >= 0) {
          this.select(this.activeIndex);
          this.moveCursorToEnd();
        }
        return;

      case 'Home':
        if (this.isOpen) {
          evt.preventDefault();
          this.moveToIndex(0);
        }
        return;

      case 'End':
        if (this.isOpen) {
          evt.preventDefault();
          this.moveToIndex(this.results.length - 1);
        }
        return;

      case 'Escape':
        if (this.isOpen) {
          evt.preventDefault();
          this.close();
        } else if (this.element.value) {
          evt.preventDefault();
          this.element.value = '';
        }
        return;

      default:
    }
  }

  handleListMouseDown(evt) {
    window.clearTimeout(this.blurTimer);
    evt.preventDefault();
  }

  handleListClick(evt) {
    const item = evt.target.closest(`.${LIST_ITEM_CLASSNAME}`);
    const index = Number(item?.dataset.index);

    if (!item || Number.isNaN(index)) {
      return;
    }

    this.select(index);
    this.form.requestSubmit();
  }

  handleViewportChange() {
    if (this.isOpen) {
      this.positionList();
    }
  }

  bindEvents() {
    const debounce = (callback, delay) => {
      let timerId = null;
      return (...args) => {
        window.clearTimeout(timerId);
        timerId = window.setTimeout(() => callback(...args), delay);
      };
    };

    const handleInput = debounce(() => {
      void this.updateSuggestions(this.element.value);
    }, DELAY);

    this.element.addEventListener('input', handleInput.bind(this));
    this.element.addEventListener('focus', this.handleFocus.bind(this));
    this.element.addEventListener('blur', this.handleBlur.bind(this));
    this.element.addEventListener('keydown', this.handleKeydown.bind(this));
    this.list.addEventListener(
      'mousedown',
      this.handleListMouseDown.bind(this),
    );
    this.list.addEventListener('click', this.handleListClick.bind(this));
    window.addEventListener('resize', this.handleViewportChange.bind(this));
    window.addEventListener(
      'scroll',
      this.handleViewportChange.bind(this),
      true,
    );
  }

  async updateSuggestions(query) {
    if (query.length < MIN_LENGTH) {
      this.close();
      return;
    }

    const suggestions = await this.getSuggestions(query);

    if (query !== this.element.value) {
      return;
    }

    this.renderResults(suggestions);
  }

  async getSuggestions(query) {
    const cachedResult = this.cache[query];
    const requestSequence = ++this.requestSequence;

    const getRequestUrl = (source, term) => {
      const separator = source.indexOf('?') === -1 ? '?' : '&';
      return `${source}${separator}${QUERY_PARAM}=${encodeURIComponent(term)}`;
    };

    if (cachedResult && cachedResult.timestamp > Date.now() - CACHE_TTL) {
      return cachedResult.value;
    }

    if (!this.options.source) {
      return [];
    }

    if (this.abortController) {
      this.abortController.abort();
    }

    const controller = window.AbortController
      ? new window.AbortController()
      : null;

    this.abortController = controller;

    try {
      const fetchOptions = {
        headers: {
          Accept: 'application/json',
        },
      };

      if (controller) {
        fetchOptions.signal = controller.signal;
      }

      const response = await window.fetch(
        getRequestUrl(this.options.source, query),
        fetchOptions,
      );

      if (requestSequence !== this.requestSequence || !response.ok) {
        return [];
      }

      const result = await response.json();
      const values = Array.isArray(result) ? result.map(String) : [];

      this.cache[query] = {
        value: values,
        timestamp: Date.now(),
      };

      return values;
    } catch {
      return [];
    } finally {
      if (controller && this.abortController === controller) {
        this.abortController = null;
      }
    }
  }

  renderResults(results) {
    this.results = results;
    this.activeIndex = -1;
    this.element.removeAttribute('aria-activedescendant');
    this.list.innerHTML = '';

    if (!results.length) {
      this.close();
      return;
    }

    const fragment = document.createDocumentFragment();

    results.forEach((value, index) => {
      const item = document.createElement('li');

      item.id = `${this.element.id}_listitem_${index}`;
      item.className = LIST_ITEM_CLASSNAME;
      item.dataset.index = String(index);
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'false');
      item.textContent = value;

      fragment.appendChild(item);
    });

    this.list.appendChild(fragment);
    this.open();
  }

  open() {
    this.list.hidden = false;
    this.positionList();
    this.element.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
    if (SCROLL_ON_FOCUS) {
      this.list.scrollIntoView({
        block: 'nearest',
      });
    }
  }

  close() {
    this.clearActiveItem();
    this.activeIndex = -1;
    this.list.hidden = true;
    this.element.setAttribute('aria-expanded', 'false');
    this.element.removeAttribute('aria-activedescendant');
    this.isOpen = false;
  }

  clearActiveItem() {
    const activeItem = this.list.querySelector(
      `.${LIST_ITEM_ACTIVE_CLASSNAME}`,
    );

    if (activeItem) {
      activeItem.classList.remove(LIST_ITEM_ACTIVE_CLASSNAME);
      activeItem.setAttribute('aria-selected', 'false');
    }
  }

  moveActive(step) {
    const items = this.list.querySelectorAll(`.${LIST_ITEM_CLASSNAME}`);

    if (!items.length) {
      return;
    }

    this.clearActiveItem();

    let nextIndex = this.activeIndex + step;

    if (nextIndex < 0) {
      nextIndex = items.length - 1;
    } else if (nextIndex >= items.length) {
      nextIndex = 0;
    }

    this.moveToIndex(nextIndex);
  }

  moveToIndex(index) {
    const items = this.list.querySelectorAll(`.${LIST_ITEM_CLASSNAME}`);

    if (!items.length || index < 0 || index >= items.length) {
      return;
    }

    this.clearActiveItem();

    const nextItem = items[index];

    this.element.setAttribute('aria-activedescendant', nextItem.id);

    nextItem.classList.add(LIST_ITEM_ACTIVE_CLASSNAME);
    nextItem.setAttribute('aria-selected', 'true');
    nextItem.scrollIntoView({ block: 'nearest' });

    this.activeIndex = index;
  }

  select(index) {
    if (index < 0 || index >= this.results.length) {
      return;
    }

    this.element.value = this.results[index];
    this.close();
  }

  moveCursorToEnd() {
    if (
      typeof this.element.setSelectionRange === 'function' &&
      typeof this.element.value === 'string'
    ) {
      const end = this.element.value.length;
      this.element.setSelectionRange(end, end);
    }
  }

  positionList() {
    const anchorRect = this.element.getBoundingClientRect();
    const offsetParent = this.list.offsetParent;
    let left = anchorRect.left + window.pageXOffset;
    let top = anchorRect.bottom + window.pageYOffset;

    if (
      offsetParent &&
      offsetParent !== document.body &&
      offsetParent !== document.documentElement
    ) {
      const parentRect = offsetParent.getBoundingClientRect();

      left =
        anchorRect.left -
        parentRect.left -
        offsetParent.clientLeft +
        offsetParent.scrollLeft;
      top =
        anchorRect.bottom -
        parentRect.top -
        offsetParent.clientTop +
        offsetParent.scrollTop;
    }

    this.list.style.left = `${left}px`;
    this.list.style.top = `${top}px`;
    this.list.style.width = `${anchorRect.width}px`;
  }
}

export function initSvAutoComplete(element, options, updateExisting) {
  // updateExisting parameter:
  // Allow explicit legacy jQuery initialization to update an instance that may
  // already have been created by automatic SearchForm initialization.

  let input;

  if (element && element.matches && element.matches(INPUT_SELECTOR)) {
    input = element;
  } else if (element && element.querySelector) {
    input = element.querySelector(INPUT_SELECTOR);
  }

  let instance = input ? instances.get(input) : null;
  if (instance) {
    if (updateExisting) {
      instance.updateOptions(options);
    }
    return instance;
  }

  const form = input ? input.form || input.closest('form') : null;

  if (!input || !form || !options || !options.source) {
    return null;
  }

  instance = new SvAutoComplete(input, options);
  instances.set(input, instance);

  return instance;
}
